import React, { useState } from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import { Link } from "react-router-dom";

const JF = "'Josefin Sans', sans-serif";
const PJ = "'Plus Jakarta Sans', sans-serif";

const PAGE_SIZE = 10;

const PLACEHOLDERS = [
  "linear-gradient(145deg,#0c1520 0%,#1a2d44 100%)",
  "linear-gradient(145deg,#120a1a 0%,#2a1040 100%)",
  "linear-gradient(145deg,#0a1a0a 0%,#142814 100%)",
  "linear-gradient(145deg,#1a1008 0%,#302010 100%)",
  "linear-gradient(145deg,#1a0808 0%,#301010 100%)",
  "linear-gradient(145deg,#0a0a1a 0%,#101030 100%)",
];

function PostRow({ post, index, colors }) {
  const [hovered, setHovered] = useState(false);

  const id = post.sys.id;
  const { title, description, headerImage } = post.fields;
  const placeholder = PLACEHOLDERS[index % PLACEHOLDERS.length];

  const d = new Date(post.sys.createdAt);
  const dateStr = [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    d.getFullYear(),
  ].join("/");

  return (
    <Link to={`/posts/${id}`} style={{ textDecoration: "none", display: "block" }}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        display="flex"
        alignItems="flex-start"
        gap="16px"
        bg={hovered ? colors.rowBgHover : colors.rowBg}
        borderRadius="8px"
        px="16px"
        py="16px"
        transition="background 0.2s ease"
        cursor="pointer"
      >
        {/* Thumbnail */}
        <Box
          flexShrink={0}
          w="90px"
          h="90px"
          borderRadius="6px"
          overflow="hidden"
          position="relative"
          sx={{ background: placeholder }}
        >
          {headerImage && (
            <Box
              as="img"
              src={headerImage}
              alt={title}
              position="absolute"
              top="0" left="0" right="0" bottom="0"
              w="100%"
              h="100%"
              objectFit="cover"
              display="block"
              sx={{
                transition: "transform 0.4s ease",
                transform: hovered ? "scale(1.06)" : "scale(1)",
              }}
            />
          )}
        </Box>

        {/* Content */}
        <Box flex="1" minW={0}>
          {/* Title row */}
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap="12px"
            mb="6px"
          >
            <Text
              fontFamily={JF}
              fontSize="17px"
              fontWeight="700"
              letterSpacing="0.1em"
              textTransform="uppercase"
              color={colors.title}
              lineHeight={1.45}
              flex={1}
            >
              {title}
            </Text>
            <Text
              fontFamily={JF}
              fontSize="13px"
              color={colors.date}
              flexShrink={0}
              whiteSpace="nowrap"
              mt="1px"
            >
              {dateStr}
            </Text>
          </Box>

          {/* Description */}
          {description && (
            <Text
              fontFamily={PJ}
              fontSize="16px"
              color={colors.desc}
              lineHeight="1.65"
              mb="8px"
            >
              {description}
            </Text>
          )}

        </Box>
      </Box>
    </Link>
  );
}

export default function Lists(props) {
  const [page, setPage] = useState(1);

  const colors = {
    rowBg:      useColorModeValue("#f0f0f0", "rgba(255,255,255,0.04)"),
    rowBgHover: useColorModeValue("#e8e8e8", "rgba(255,255,255,0.08)"),
    title:      useColorModeValue("#222222", "#dddddd"),
    date:       useColorModeValue("#aaaaaa", "#555555"),
    desc:       useColorModeValue("#666666", "#888888"),
    tag:        useColorModeValue("#bbbbbb", "#444444"),
    heading:    useColorModeValue("#222222", "#dddddd"),
  };
  const pageActive = useColorModeValue("#1a1714", "#e4e0d8");
  const pageIdle   = useColorModeValue("#c0bbb0", "#35322e");
  const pageHover  = useColorModeValue("#5a5550", "#888078");

  const lists      = props.postLists;
  const totalPages = Math.ceil(lists.length / PAGE_SIZE);
  const paginated  = lists.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goTo = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box as="section" mt={6} mb={12}>
      {/* Heading */}
      <Text
        fontFamily={JF}
        fontSize="13px"
        fontWeight="700"
        letterSpacing="0.2em"
        textTransform="uppercase"
        color={colors.heading}
        mb={5}
      >
        Latest Content
      </Text>

      {/* List */}
      <Box display="flex" flexDirection="column" gap="10px">
        {paginated.map((post, i) => (
          <PostRow
            key={post.sys.id}
            post={post}
            index={(page - 1) * PAGE_SIZE + i}
            colors={colors}
          />
        ))}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="4px" mt={10}>
          <button
            onClick={() => goTo(page - 1)}
            disabled={page === 1}
            style={{
              background: "none", border: "none",
              cursor: page === 1 ? "default" : "pointer",
              fontFamily: JF, fontSize: "12px", fontWeight: "600",
              letterSpacing: "0.2em", textTransform: "uppercase",
              color: page === 1 ? pageIdle : pageHover,
              opacity: page === 1 ? 0.35 : 1,
              padding: "4px 8px", transition: "color 0.2s ease",
            }}
          >
            ← Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => goTo(p)} style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: JF, fontSize: "12px",
              fontWeight: p === page ? "700" : "400",
              letterSpacing: "0.2em",
              color: p === page ? pageActive : pageIdle,
              padding: "4px 6px", transition: "color 0.2s ease",
            }}>
              {p}
            </button>
          ))}

          <button
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages}
            style={{
              background: "none", border: "none",
              cursor: page === totalPages ? "default" : "pointer",
              fontFamily: JF, fontSize: "12px", fontWeight: "600",
              letterSpacing: "0.2em", textTransform: "uppercase",
              color: page === totalPages ? pageIdle : pageHover,
              opacity: page === totalPages ? 0.35 : 1,
              padding: "4px 8px", transition: "color 0.2s ease",
            }}
          >
            Next →
          </button>
        </Box>
      )}
    </Box>
  );
}
